import { type OutputOperationResult } from 'api/types';

import type { AnyRecord } from './records/types';

export type Nullable<T> = T | null | undefined;

export type NonNullableProps<T> = {
  [K in keyof T]: Exclude<T[K], null | undefined>;
};

export type NullableProps<T> = {
  [K in keyof T]: Nullable<T[K]>;
};

export type ExcludeFunctions<T> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [K in keyof T as T[K] extends Function ? never : K]: T[K];
};

/**
 * A utility type that creates a branded type from the base type `T`.
 * This can be used to create nominal typing in TypeScript, which is useful for type safety
 * where structurally identical types should be treated as distinct.
 *
 * It won't impact the runtime behavior of the type, nor make a difference on the production bundle,
 * but it will help TypeScript to differentiate between types.
 */
export type Unique<T, Brand extends string> = T & { readonly __brand: Brand };

export function hasValue<T>(value: Nullable<T>): value is T {
  return value !== null && value !== undefined;
}

/**
 * Asserts that a value is defined (not null or undefined).
 * Use this in contexts where TypeScript cannot infer that a value is defined
 * but you know it is (e.g., in React Query's queryFn when guarded by enabled).
 */
export function assertDefined<T>(value: Nullable<T>, errorMessage?: string): T {
  if (!hasValue(value)) {
    throw new Error(errorMessage || 'Value is unexpectedly undefined or null');
  }
  return value;
}

/** Transform an OutputOperationResult object to the desired type with the given adaptor. */
export const transform = async <T, K>(
  response: OutputOperationResult<T>,
  adaptor: (response: T) => K | Promise<K>
): Promise<OutputOperationResult<K>> => {
  if (response.success) {
    return {
      ...response,
      result: await adaptor(response.result)
    };
  } else {
    return response;
  }
};

export function isObject<T>(value: unknown): value is AnyRecord<string, T> {
  return hasValue(value) && typeof value === 'object';
}

export type Updater<T, Input = T> = T | ((input: Input) => T);

/**
 * Type guard to check if a value is a non-function value.
 */
export function isValue<T, Input = T>(value: Updater<T, Input>): value is T {
  return typeof value !== 'function';
}
